import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../lib/cart";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/seo";

const EMPTY = {
  name: "", email: "", phone: "", contact_pref: "whatsapp",
  country: "", city: "", postal_code: "", address: "", notes: "", website: "",
};

const COUNTRIES = ["España", "Países Bajos", "Alemania", "Francia", "Italia", "Portugal", "Bélgica", "Reino Unido", "Estados Unidos"];

/**
 * Order request: no payment here. The shop checks availability, quotes shipping and closes the
 * sale by WhatsApp or email with a PayPal link.
 */
export default function Checkout() {
  const { lang } = useI18n();
  const tx = (es, en) => (lang === "en" ? en : es);
  const { items, ids, subtotal, clear, remove } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(null);
  useSEO({ title: tx("Hacer pedido · Maggie Records", "Place order · Maggie Records") });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, listing_ids: ids });
      setPlaced({ code: data.code, subtotal: data.subtotal, email: form.email, contact_pref: form.contact_pref, count: items.length });
      clear();
      setForm(EMPTY);
      window.scrollTo({ top: 0 });
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && Array.isArray(data?.unavailable)) {
        data.unavailable.forEach((id) => remove(id));
        toast.error(tx("Algún disco acaba de venderse y lo hemos quitado del carrito. Revisa el pedido.", "A record has just been sold and was removed from your cart. Please review your order."), { duration: 10000 });
      } else {
        toast.error(data?.detail || tx("No se pudo enviar el pedido. Inténtalo de nuevo.", "Couldn't send the order. Please try again."), { duration: 10000 });
      }
    } finally {
      setLoading(false);
    }
  };

  if (placed) {
    return (
      <div className="max-w-[720px] mx-auto px-5 md:px-10 pt-14 md:pt-20" data-testid="order-success">
        <p className="text-xs text-muted-foreground mb-2">{tx("Pedido recibido", "Order received")}</p>
        <h1 className="text-3xl md:text-4xl font-medium mb-5">{tx("¡Gracias! Tu pedido es el", "Thank you! Your order is")} {placed.code}.</h1>
        <div className="text-sm leading-relaxed text-foreground/90 space-y-3 max-w-xl">
          <p>
            {tx("Todavía no has pagado nada. Vamos a comprobar que", "You haven't paid anything yet. We'll check that")}{" "}
            {placed.count === 1 ? tx("el disco está disponible", "the record is available") : tx("los discos están disponibles", "the records are available")}{" "}
            {tx("y a calcular el envío.", "and calculate shipping.")}
          </p>
          <p>
            {tx("Te escribiremos", "We'll contact you")} {placed.contact_pref === "whatsapp" ? tx("por WhatsApp", "on WhatsApp") : tx("por email", "by email")}{" "}
            {tx("con el precio total y el enlace de pago por PayPal.", "with the total price and a PayPal payment link.")}
          </p>
          <p className="text-muted-foreground">{tx("Te hemos enviado una copia del pedido a", "We've sent a copy of your order to")} {placed.email}.</p>
        </div>
        <div className="mt-10 flex flex-wrap gap-6 text-sm">
          <Link to="/catalogo" className="vmr-link-underline">{tx("Seguir mirando discos →", "Keep browsing →")}</Link>
          <Link to="/envios" className="vmr-link-underline">{tx("Política de envíos", "Shipping policy")}</Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-[720px] mx-auto px-5 md:px-10 pt-14" data-testid="checkout-empty">
        <h1 className="text-3xl font-medium mb-4">{tx("Tu carrito está vacío", "Your cart is empty")}</h1>
        <Link to="/catalogo" className="vmr-link-underline text-sm">{tx("Ver catálogo →", "Browse the catalogue →")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-10 md:pt-14" data-testid="checkout-page">
      <div className="border-b border-border pb-5 mb-10">
        <Link to="/carrito" className="text-xs text-muted-foreground hover:text-foreground">← {tx("Volver al carrito", "Back to cart")}</Link>
        <h1 className="text-3xl md:text-4xl font-medium mt-3">{tx("Hacer pedido", "Place order")}</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          {tx(
            "No pagas nada ahora. Revisamos tu pedido, calculamos el envío y te escribimos con el total para pagar por PayPal.",
            "You don't pay anything now. We review your order, calculate shipping and contact you with the total to pay by PayPal.",
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
        <form onSubmit={submit} className="md:col-span-3 flex flex-col gap-5" data-testid="order-form">
          <fieldset className="flex flex-col gap-4">
            <legend className="text-sm font-medium mb-3">{tx("Tus datos", "Your details")}</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="order-name" label={tx("Nombre y apellidos", "Full name")} value={form.name} onChange={set("name")} required autoComplete="name" />
              <Field id="order-email" label="Email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" />
            </div>
            <Field
              id="order-phone"
              label={tx("Teléfono / WhatsApp con prefijo", "Phone / WhatsApp with country code")}
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              required={form.contact_pref === "whatsapp"}
              placeholder="+34 600 000 000"
              autoComplete="tel"
            />
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted-foreground">{tx("¿Cómo prefieres que te contactemos?", "How should we contact you?")}</span>
              <div className="flex gap-3" role="radiogroup">
                {[
                  ["whatsapp", "WhatsApp"],
                  ["email", "Email"],
                ].map(([value, label]) => (
                  <label
                    key={value}
                    className={`flex-1 border px-4 py-2.5 text-sm cursor-pointer text-center ${form.contact_pref === value ? "border-foreground" : "border-border hover:border-foreground/50"}`}
                  >
                    <input type="radio" name="contact_pref" value={value} checked={form.contact_pref === value} onChange={() => set("contact_pref")(value)} className="sr-only" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-4">
            <legend className="text-sm font-medium mb-3">{tx("Envío", "Shipping")}</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-muted-foreground" htmlFor="order-country">{tx("País", "Country")}</label>
                <input
                  id="order-country"
                  list="order-countries"
                  value={form.country}
                  onChange={(e) => set("country")(e.target.value)}
                  required
                  autoComplete="country-name"
                  className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
                  data-testid="order-country"
                />
                <datalist id="order-countries">
                  {COUNTRIES.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <Field id="order-city" label={tx("Ciudad", "City")} value={form.city} onChange={set("city")} autoComplete="address-level2" />
              <Field id="order-postal" label={tx("Código postal", "Postal code")} value={form.postal_code} onChange={set("postal_code")} autoComplete="postal-code" />
              <Field id="order-address" label={tx("Dirección", "Address")} value={form.address} onChange={set("address")} autoComplete="street-address" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="order-notes">{tx("Comentarios (opcional)", "Notes (optional)")}</label>
              <textarea
                id="order-notes"
                value={form.notes}
                onChange={(e) => set("notes")(e.target.value)}
                rows={3}
                className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
                data-testid="order-notes"
              />
            </div>
          </fieldset>

          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" />

          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto sm:self-start bg-foreground text-background px-8 py-3.5 text-sm hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-50"
            data-testid="order-submit"
          >
            {loading ? tx("Enviando pedido…", "Sending order…") : tx("Enviar pedido", "Send order")}
          </button>
          <p className="text-xs text-muted-foreground">
            {tx("Al enviar aceptas que usemos estos datos solo para gestionar tu pedido.", "By sending you agree we use these details only to handle your order.")}{" "}
            <Link to="/envios" className="underline underline-offset-4">{tx("Política de envíos", "Shipping policy")}</Link>
          </p>
        </form>

        <aside className="md:col-span-2 border border-border p-5 h-fit" data-testid="order-summary">
          <h2 className="text-sm font-medium mb-4">{tx("Tu pedido", "Your order")} · {items.length} {items.length === 1 ? tx("disco", "record") : tx("discos", "records")}</h2>
          <ul className="divide-y divide-border mb-4">
            {items.map((it) => (
              <li key={it.listing_id} className="flex items-center gap-3 py-2.5">
                <img src={it.thumb || "/brand/cover-placeholder.png"} alt="" className="w-11 h-11 object-cover bg-[hsl(var(--muted))]" />
                <span className="flex-1 min-w-0">
                  <span className="block text-xs text-muted-foreground truncate">{it.artist}</span>
                  <span className="block text-sm truncate">{it.title}</span>
                </span>
                <span className="text-sm tabular-nums">{formatPrice(it.price, it.currency)}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="tabular-nums font-medium">{formatPrice(subtotal, "EUR")}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {tx("Envío aparte: te lo confirmamos antes de pagar.", "Shipping extra: confirmed before you pay.")}{" "}
            <Link to="/envios" className="underline underline-offset-4">{tx("Ver tarifas", "See rates")}</Link>
          </p>
        </aside>
      </div>
    </div>
  );
}

function Field({ id, label, value, onChange, type = "text", required, placeholder, autoComplete }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-muted-foreground" htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
        data-testid={id}
      />
    </div>
  );
}
